import React from 'react';
import useLocales from '../hooks/useLocales';
import { Column } from "@devexpress/dx-react-grid";
import { BackendTypeName } from './Utils/backendDataParser'
import useTerminology from '../hooks/useTerminology';

import { OrderType as OrderTypeEnum, OrderInvoiceStatus as OrderInvoiceStatusEnum, DateTypeEnum, OrderTypeEnumToInvoices} from '../Common/enums';
import { useSelector } from 'react-redux';
import { AppState } from './Reducer/rootReducerTypes';
import { shouldPermissionExist } from '../utils/userInfoKeyValuePairChecker';

export const handymanOrderListFilterColumns = ['externalId', 'orderName', 'location', 'customerName', 'equipmentName'];
export const handymanQuoteListFilterColumns = ['backOfficeId', 'quoteName', 'pricingType', 'customerPrice', 'validUntil', 'customerName', 'equipmentName', 'equipmentSerial', 'orderTypeLabel', 'description', 'signatureName', 'signatureDate', 'rejectReason'];

export function ColumnListState() {
  const selectedClientJson = window.localStorage.getItem('selectedClient');
  const { translate } = useLocales();
  const terminologies = useTerminology();
  const userInfo = useSelector((state: AppState) => state.userInfo.userInformation);
  const shouldActivityPermissionsExist = shouldPermissionExist(userInfo.userPermissions, 'activitySettingsPermissions');

  const handymanColumns: Column[] =
    [
      { name: 'status', title: translate("orderListView." + "Status") },
      { name: 'externalId', title: translate("orderListView." + 'External Id') },
      { name: 'orderName', title: translate("orderListView." + 'Order Name') },
      { name: 'description', title: translate("orderListView." + 'Description') },
      { name: 'location', title: translate("orderListView." + 'Location') },
      { name: 'equipmentName', title: translate("orderListView." + 'Site/Equipment') },
      { name: 'siteId', title: translate("orderListView." + 'Site/Equipment Id') },
      { name: 'orderNo', title: translate("orderListView." + 'Order Id') },
      { name: 'createdDate', title: translate("orderListView." + 'Created Date') },
      { name: 'startDate', title: translate("orderListView." + 'Start Date') },
      { name: 'endDate', title: translate("orderListView." + 'End Date') },
      { name: 'plannedStartDate', title: translate("orderListView." + 'Planned Start Date') },
      { name: 'plannedEndDate', title: translate("orderListView." + 'Planned End Date') },
      { name: 'priority', title: translate("orderListView." + 'Priority') },
      { name: 'orderCategory', title: translate("common." + 'Category') },
      { name: 'customerName', title: translate("orderListView." + 'Customer Name') },
      { name: 'customerReference', title: terminologies ? terminologies.customerReference.singular : translate('orderListView.Customer Reference') },
      { name: 'mainOrder', title: terminologies ? terminologies.mainOrder.singular : translate('orderListView.Main Order') },
      { name: 'requisition', title: terminologies ? terminologies.requisition.singular : translate('orderListView.Requisition') },
      { name: 'orderType', title: translate("orderListView." + 'Order Type') },
      { name: 'urgent', title: translate("addJobView." + 'Urgent') },
      { name: 'secondaryCustomerName', title: terminologies ? terminologies.customer2.singular : translate("orderListView." + 'SecondaryCustomerName') }
    ].concat(shouldActivityPermissionsExist ? [{ name: 'activityType', title: translate("orderListView." + 'activityType') }] : []);

  const smartdayColumns: Column[] = [
    { name: 'status', title: translate("orderListView." + "Status") },
    { name: 'orderNo', title: translate("orderListView." + 'Order Id') },
    { name: 'description', title: translate("orderListView." + 'Description') },
    { name: 'createdDate', title: translate("orderListView." + 'Created Date') },
    { name: 'startDate', title: translate("orderListView." + 'Start Date') },
    { name: 'endDate', title: translate("orderListView." + 'End Date') },
    { name: 'siteName', title: translate("orderListView." + 'Site/Equipment') },
    { name: 'externalId', title: translate("orderListView." + 'External Id') },
    { name: 'siteId', title: translate("orderListView." + 'Site/Equipment Id') }
  ];

  if (selectedClientJson) {
    const selectedClient = JSON.parse(selectedClientJson);

    if (selectedClient.backendName === BackendTypeName.Smartday) {
      return smartdayColumns;
    }

    return handymanColumns;
  }

  return handymanColumns;
}

export function ColumnGroupState() {
  const selectedClientJson = window.localStorage.getItem('selectedClient');
  const terminologies = useTerminology();
  const { translate } = useLocales();

  const handymanColumns: Column[] =
    [
      { name: 'status', title: translate("orderListView." + "Status") },
      { name: 'createdDate', title: translate("orderListView." + 'Created Date') },
      { name: 'startDate', title: translate("orderListView." + 'Start Date') },
      { name: 'endDate', title: translate("orderListView." + 'End Date') },
      { name: 'plannedStartDate', title: translate("orderListView." + 'Planned Start Date') },
      { name: 'plannedEndDate', title: translate("orderListView." + 'Planned End Date') },
      { name: 'priority', title: translate("orderListView." + 'Priority') },
      { name: 'orderCategory', title: translate("common." + 'Category') },
      { name: 'customerName', title: translate("orderListView." + 'Customer Name') },
      { name: 'orderType', title: translate("orderListView." + 'Order Type') },
      { name: 'equipmentName', title: translate("orderListView." + 'Site/Equipment') },
      { name: 'secondaryCustomerName', title: terminologies ? terminologies.customer2.singular : translate("orderListView." + 'SecondaryCustomerName') }
    ];

  const smartdayColumns: Column[] = [
    { name: 'status', title: translate("orderListView." + "Status") },
    { name: 'createdDate', title: translate("orderListView." + 'Created Date') },
    { name: 'startDate', title: translate("orderListView." + 'Start Date') },
    { name: 'endDate', title: translate("orderListView." + 'End Date') },
    { name: 'siteName', title: translate("orderListView." + 'Site/Equipment') }
  ];

  if (selectedClientJson) {
    const selectedClient = JSON.parse(selectedClientJson);

    if (selectedClient.backendName === BackendTypeName.Smartday) {
      return smartdayColumns;
    }

    return handymanColumns;
  }

  return handymanColumns;
}

export const handymanInvoiceListFilterColumns =
  [
    'customerName',
    'customerBackOfficeId',
    'invoiceNumber',
    'orderNo',
    'orderName'
  ];

export function ColumnInvoiceListState() {
  const { translate } = useLocales();
  const terminologies = useTerminology();

  const handymanColumns: Column[] =
    [
      { name: 'orderInvoiceStatus', title: translate('invoiceListView.OrderInvoiceStatus') },
      { name: 'invoiceType', title: translate('invoiceListView.InvoiceType') },
      { name: 'invoiceNumber', title: translate('invoiceListView.InvoiceNo') },
      { name: 'orderNo', title: translate('invoiceListView.OrderNo') },
      { name: 'orderName', title: terminologies && terminologies.orderName ? terminologies.orderName.singular : translate('invoiceListView.OrderName') },
      { name: 'departmentBackofficeId', title: terminologies && terminologies.department ? terminologies.department.singular + ' ' + translate('addJobView.No').toLowerCase() : translate('invoiceListView.DepartmentNo') },
      { name: 'departmentName', title: terminologies && terminologies.department ? terminologies.department.singular : translate('invoiceListView.DepartmentName') },
      { name: 'customerBackOfficeId', title: translate('invoiceListView.CustomerNo') },
      { name: 'customerName', title: translate('invoiceListView.Customer') },
      { name: 'contractBackOfficeId', title: translate('invoiceListView.ContractNo') },
      { name: 'contractName', title: translate('invoiceListView.ContractName') },
      { name: 'invoiceDate', title: translate('invoiceListView.InvoiceDate') },
      { name: 'description', title: translate('invoiceListView.Description') },
      { name: 'dueDate', title: translate('invoiceListView.DueDate') },
      { name: 'sumMaterial', title: translate('invoiceListView.SumMaterial') },
      { name: 'sumWork', title: translate('invoiceListView.SumHours') },
      { name: 'invoiceAmount', title: translate('invoiceListView.Amount') },
      { name: 'theirReference', title: translate('invoiceListView.YourRef') },
      { name: 'ourReference', title: translate('invoiceListView.OurRef') },
      { name: 'vat', title: translate('invoiceListView.VAT') },
      { name: 'vatPercentage', title: translate('invoiceListView.VATPercentage') },
      { name: 'paymentDate', title: translate('invoiceListView.PayDate') },
      { name: 'placeOfDelivery', title: translate('invoiceListView.DeliveryAddress') },
      { name: 'invoiceDocument', title: translate('invoiceListView.ShowInvoices') },
      { name: 'invoiceAttachments', title: translate('invoiceListView.ShowAttachments') },
      { name: 'invoiceLinkToOrder', title: translate('invoiceListView.ShowOrder') },
    ];

  return handymanColumns;
}


export function ColumnDiscrepancyListState() {
  const { translate } = useLocales();

  return [
    { name: 'status', title: translate('common.Status') },
    { name: 'requestForClosing', title: translate('DiscrepancyList.RequestedClosed') },
    { name: 'date', title: translate('common.Date') },
    { name: 'id', title: translate('common.Id') },
    { name: 'externalId', title: translate('DiscrepancyList.ExternalId') },
    { name: 'name', title: translate('common.Name') },
    { name: 'discrepancyType', title: translate('DiscrepancyList.DiscrepancyType') },
    { name: 'assetName', title: translate('DiscrepancyList.Asset') },
    { name: 'nextServiceDate', title: translate('DiscrepancyList.NextServiceDate') },
    { name: 'severity', title: translate('DiscrepancyList.Severity') },
    { name: 'origin', title: translate('DiscrepancyList.ServiceForm') },
    { name: 'linkToOriginOrder', title: translate('DiscrepancyList.ShowOriginOrder') },
    { name: 'requestedAction', title: translate('DiscrepancyList.Request') },
    { name: 'originOrderName', title: translate('DiscrepancyList.OriginOrderName') },
    { name: 'classificationName', title: translate('DiscrepancyList.ClassificationName') },
    { name: 'departmentName', title: translate('DiscrepancyList.DepartmentName') }
  ];
}

export function ColumnDiscrepanyListGroupState() {
  const { translate } = useLocales();

  return [
    { name: 'assetName', title: translate('DiscrepancyList.Asset') },
    { name: 'origin', title: translate('DiscrepancyList.ShowOriginOrder') },
    { name: 'severity', title: translate('DiscrepancyList.Severity') },
    { name: 'status', title: translate('DiscrepancyList.Status') },
    { name: 'discrepancyType', title: translate('DiscrepancyList.DiscrepancyType') },
    { name: 'classificationName', title: translate('DiscrepancyList.ClassificationName') },
    { name: 'departmentName', title: translate('DiscrepancyList.DepartmentName') }
  ];
}

export const discrepancyListFilterColumns =
  [
    'id',
    'externalId',
    'date',
    'name',
    'discrepancyType',
    'assetName',
    'performedBy',
    'orderNo',
    'origin',
    'assetId',
    'originOrderName'
  ];

export function ColumnInvoiceListStateOnOrderLevel() {
  const { translate } = useLocales();
  const terminologies = useTerminology();

  const handymanColumns: Column[] =
    [
      { name: 'orderInvoiceStatus', title: translate('invoiceListView.OrderInvoiceStatus') },
      { name: 'invoiceType', title: translate('invoiceListView.InvoiceType') },
      { name: 'invoiceNumber', title: translate('invoiceListView.InvoiceNo') },
      { name: 'payingCustomerBackOfficeId', title: terminologies && terminologies.customer2 && terminologies.customer2.singular !== '' ? terminologies.customer2.singular + ' ' + translate('addJobView.No').toLowerCase() : translate('invoiceListView.Customer2No') },
      { name: 'payingCustomerName', title: terminologies && terminologies.customer2 && terminologies.customer2.singular !== '' ? terminologies.customer2.singular + ' ' + translate('Name').toLowerCase() : translate('invoiceListView.Customer2Name') },
      { name: 'invoiceDate', title: translate('invoiceListView.InvoiceDate') },
      { name: 'description', title: translate('invoiceListView.Description') },
      { name: 'dueDate', title: translate('invoiceListView.DueDate') },
      { name: 'sumMaterial', title: translate('invoiceListView.SumMaterial') },
      { name: 'sumWork', title: translate('invoiceListView.SumHours') },
      { name: 'invoiceAmount', title: translate('invoiceListView.Amount') },
      { name: 'theirReference', title: translate('invoiceListView.YourRef') },
      { name: 'ourReference', title: translate('invoiceListView.OurRef') },
      { name: 'vat', title: translate('invoiceListView.VAT') },
      { name: 'vatPercentage', title: translate('invoiceListView.VATPercentage') },
      { name: 'paymentDate', title: translate('invoiceListView.PayDate') },
      { name: 'placeOfDelivery', title: translate('invoiceListView.DeliveryAddress') },
      { name: 'invoiceDocument', title: translate('invoiceListView.ShowInvoices') },
      { name: 'invoiceAttachments', title: translate('invoiceListView.ShowAttachments') },
    ];

  return handymanColumns;
}

export function ColumnInvoiceGroupState() {
  const { translate } = useLocales();
  const terminologies = useTerminology();

  const handymanColumns: Column[] =
    [
      { name: 'departmentName', title: terminologies && terminologies.department && terminologies.department.singular ? terminologies.department.singular : translate('invoiceListView.Department') },
      { name: 'orderNo', title: translate('invoiceListView.OrderNo') },
    ];

  return handymanColumns;
}

export function ColumnInvoiceGroupStateOnOrderLevel() {
  const { translate } = useLocales();

  const handymanColumns: Column[] =
    [
      { name: 'invoiceType', title: translate('invoiceListView.InvoiceType') },
    ];

  return handymanColumns;
}

export function GetOrderInvoiceStatuses() {
  const orderInvoiceStatuses: any = Object.keys(OrderInvoiceStatusEnum)
    .filter((v) => isNaN(Number(v)))
    .map((name) => {
      return {
        id: OrderInvoiceStatusEnum[name as keyof typeof OrderInvoiceStatusEnum],
        name,
      };
    });

  return orderInvoiceStatuses;
}

export function GetDateTypes() {
  const dateTypes: any = Object.keys(DateTypeEnum)
    .filter((v) => isNaN(Number(v)))
    .map((name) => {
      return {
        id: DateTypeEnum[name as keyof typeof DateTypeEnum],
        name,
      };
    });

  return dateTypes;
}

export function GetOrderTypes() {
  const orderTypes: any = Object.keys(OrderTypeEnumToInvoices)
    .filter((v) => isNaN(Number(v)))
    .map((name) => {
      return {
        id: OrderTypeEnumToInvoices[name as keyof typeof OrderTypeEnumToInvoices],
        name,
      };
    });

  return orderTypes;
}

export function ColumnOrderState(defaultValue: any, key: any) {
  const [value, setValue] = React.useState(() => {
    const stickyValue = window.localStorage.getItem(key);
    return stickyValue !== null
      ? JSON.parse(stickyValue)
      : defaultValue;
  });
  React.useEffect(() => {
    window.localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);
  return [value, setValue];
}

export function ColumnVisibilityState(defaultValue: any, key: any) {
  const [value, setValue] = React.useState(() => {
    const stickyValue = window.localStorage.getItem(key);
    return stickyValue !== null
      ? JSON.parse(stickyValue)
      : defaultValue;
  });
  React.useEffect(() => {
    window.localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);
  return [value, setValue];
}

export function ListSortBy(defaultValue: any, key: any) {
  const [value, setValue] = React.useState(() => {
    const stickyValue = window.localStorage.getItem(key);
    return stickyValue !== null
      ? JSON.parse(stickyValue)
      : defaultValue;
  });
  React.useEffect(() => {
    window.localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);
  return [value, setValue];
}

export function ColumnWidthState(defaultValue: any, key: any) {
  const [value, setValue] = React.useState(() => {
    const stickyValue = window.localStorage.getItem(key);
    return stickyValue !== null
      ? JSON.parse(stickyValue)
      : defaultValue;
  });
  React.useEffect(() => {
    window.localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);
  return [value, setValue];
}

export function SelectClient(currentValue: any, key: any) {
  const localStorageValue = window.localStorage.getItem(key);
  if ((localStorageValue && localStorageValue !== currentValue) || localStorageValue === null || localStorageValue === undefined) {
    window.localStorage.setItem(key, JSON.stringify(currentValue));
  }

  if (localStorageValue !== null) { // only need to reload the page if a client was already selected
    window.location.reload();
  }
}

export function ClientOptions(currentValue: any, key: any) {
  const localStorageValue = window.localStorage.getItem(key);
  if ((localStorageValue && localStorageValue !== currentValue) || localStorageValue === null || localStorageValue === undefined) {
    window.localStorage.setItem(key, JSON.stringify(currentValue));
  }
}

export function ClearLocalStorageItem(itemName: string) {
  window.localStorage.removeItem(itemName);
}